import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, Card, Btn, Input, Select, EmptyState, Pill } from "../components/UI";
import { Clapperboard, Plus, Trash2, Image as ImageIcon, Mic, Video, LayoutGrid, PackageCheck } from "lucide-react";
import { toast } from "sonner";

const AssetChip = ({ ok, icon: Icon, label }) => (
  <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border ${ok ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-300" : "border-white/10 bg-white/5 text-slate-500"}`}>
    <Icon className="w-3 h-3" /> {label}
  </span>
);

export default function AdStudio() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [entities, setEntities] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", candidate_name: "", party: "Independent", committee_name: "", entity_id: "" });

  const load = () => {
    api.get("/ad-studio/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
    api.get("/political-entities").then((r) => setEntities(r.data || [])).catch(() => {});
  };
  useEffect(load, []);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const create = async () => {
    if (!form.name.trim()) return toast.error("Give the project a name");
    setCreating(true);
    try {
      const r = await api.post("/ad-studio/projects", form);
      toast.success("Ad project created");
      navigate(`/ad-studio/${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create project");
    } finally {
      setCreating(false);
    }
  };

  const del = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this ad project and all generated assets?")) return;
    await api.delete(`/ad-studio/projects/${id}`);
    toast.success("Project deleted");
    load();
  };

  const videoStatus = (p) => {
    const v = p.assets?.video_15 || p.assets?.video_30;
    if (!v) return null;
    return v.url ? "ready" : v.status;
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="ad-studio-page">
      <PageHeader
        eyebrow="Create"
        title="Political Ad Studio"
        subtitle="Turn a candidate photo + script into a full ad package: AI talking video (HeyGen), cloned or stock voiceover (ElevenLabs), IAB banner set, and a trafficking-ready ZIP — with 'Paid for by' disclosures baked in."
        action={<Btn data-testid="new-ad-project-btn" onClick={() => setShowNew(!showNew)}><Plus className="w-4 h-4" /> New Ad Project</Btn>}
      />

      {showNew && (
        <Card className="mb-6" data-testid="new-project-form">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Input label="Project name" placeholder="Voss for Senate — Launch Spot" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="project-name-input" />
            <Input label="Candidate name" placeholder="Jordan Voss" value={form.candidate_name} onChange={(e) => set("candidate_name", e.target.value)} data-testid="candidate-name-input" />
            <Select label="Party" value={form.party} onChange={(e) => set("party", e.target.value)} data-testid="party-select">
              <option>Democrat</option><option>Republican</option><option>Independent</option>
            </Select>
            <Select label="Committee (organization)" value={form.entity_id} data-testid="entity-select"
              onChange={(e) => {
                const ent = entities.find((x) => x.id === e.target.value);
                setForm((p) => ({ ...p, entity_id: e.target.value, committee_name: ent ? (ent.committee_name || ent.public_campaign_name || ent.legal_org_name) : p.committee_name }));
              }}>
              <option value="">— select or type below —</option>
              {entities.map((ent) => <option key={ent.id} value={ent.id}>{ent.committee_name || ent.public_campaign_name || ent.legal_org_name}</option>)}
            </Select>
          </div>
          <div className="mt-4 flex items-end gap-4">
            <Input className="flex-1" label='Committee name (for "Paid for by …" disclaimer)' placeholder="Voss for Senate Committee" value={form.committee_name} onChange={(e) => set("committee_name", e.target.value)} data-testid="committee-input" />
            <Btn onClick={create} disabled={creating} data-testid="create-project-btn">{creating ? "Creating…" : "Create & open studio"}</Btn>
          </div>
        </Card>
      )}

      {projects === null ? (
        <div className="text-slate-400 text-sm py-16 text-center">Loading projects…</div>
      ) : projects.length === 0 ? (
        <Card>
          <EmptyState icon={Clapperboard} title="No ad projects yet"
            description="Create your first AI ad: upload a candidate photo, write a 15s/30s script, pick a voice, and export a launch-ready package."
            action={<Btn onClick={() => setShowNew(true)}><Plus className="w-4 h-4" /> New Ad Project</Btn>} />
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((p) => (
            <Card key={p.id} className="cursor-pointer hover:border-cyan-400/30 transition-colors" data-testid={`project-card-${p.id}`} onClick={() => navigate(`/ad-studio/${p.id}`)}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-display font-semibold truncate">{p.name}</div>
                  <div className="text-xs text-slate-400 mt-0.5 truncate">{p.candidate_name || "—"} · {p.party}</div>
                </div>
                <button className="text-slate-500 hover:text-rose-300 p-1" onClick={(e) => del(p.id, e)} data-testid={`delete-project-${p.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              {p.assets?.photo?.url && (
                <img src={p.assets.photo.url} alt="" className="mt-3 w-full h-28 object-cover rounded-md border border-white/10" />
              )}
              <div className="mt-3 flex flex-wrap gap-1.5">
                <AssetChip ok={!!p.assets?.photo} icon={ImageIcon} label="Photo" />
                <AssetChip ok={!!(p.assets?.vo_15 || p.assets?.vo_30)} icon={Mic} label="Voiceover" />
                <AssetChip ok={videoStatus(p) === "ready"} icon={Video} label={videoStatus(p) && videoStatus(p) !== "ready" ? `Video: ${videoStatus(p)}` : "Video"} />
                <AssetChip ok={(p.assets?.banners || []).length > 0} icon={LayoutGrid} label={`Banners ${(p.assets?.banners || []).length || ""}`} />
                <AssetChip ok={!!p.assets?.export} icon={PackageCheck} label="ZIP" />
              </div>
              <div className="mt-3 flex items-center justify-between">
                <Pill status={p.status === "exported" ? "Completed" : "Draft"}>{p.status === "exported" ? "Exported" : "Draft"}</Pill>
                <span className="text-[10px] text-slate-500">{new Date(p.created_at).toLocaleDateString()}</span>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
